import { useState } from 'react';
import { useSetPriceAdjustment } from '@/lib/channels';
import type { ApartmentChannelMapping, Provider } from '@/lib/channels';
import { ApiError, messageFromError } from '@/lib/api';
import { useToast } from '@/components/ToastProvider';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AirbnbSettingsEditor } from './AirbnbSettingsEditor';

interface Props {
  apartmentID: string;
  provider: Provider;
  mapping: ApartmentChannelMapping | undefined;
  canWrite: boolean;
}

// bps → display percent. 100 bps = 1%.
function bpsToPercent(bps: number): string {
  return (bps / 100).toFixed(2).replace(/\.?0+$/, '');
}

// Percent string → bps integer. round(percent * 100).
function percentToBps(s: string): number | null {
  const v = parseFloat(s);
  if (!isFinite(v)) return null;
  return Math.round(v * 100);
}

export function SettingsTab({ apartmentID, provider, mapping, canWrite }: Props) {
  const { toast } = useToast();
  const [percent, setPercent] = useState<string>(
    mapping ? bpsToPercent(mapping.price_adjustment_bps) : '0',
  );
  const [err, setErr] = useState<string | null>(null);

  const setPriceAdj = useSetPriceAdjustment(apartmentID);

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const bps = percentToBps(percent);
    if (bps === null) {
      setErr('Enter a valid number (e.g. −5 for −5%, 0 to disable).');
      return;
    }
    setPriceAdj.mutate(
      { provider, body: { price_adjustment_bps: bps } },
      {
        onSuccess: () =>
          toast({ kind: 'success', title: 'Price adjustment saved' }),
        onError: (e) => {
          if (e instanceof ApiError && e.status === 404) {
            toast({
              kind: 'error',
              title: 'Configure or connect this channel before setting a price adjustment',
            });
          } else {
            setErr(messageFromError(e));
          }
        },
      },
    );
  }

  return (
    <div className="space-y-5">
      {canWrite ? (
        <form onSubmit={handleSave} className="space-y-4">
          <Input
            label="Price adjustment (%)"
            type="number"
            step="0.01"
            value={percent}
            onChange={(e) => setPercent((e.target as HTMLInputElement).value)}
            hint="Positive = mark-up, negative = discount. e.g. −50 for 50% off. Stored as basis points (1% = 100 bps)."
            error={err ?? undefined}
          />

          <Button type="submit" variant="primary" size="sm" loading={setPriceAdj.isPending}>
            Save
          </Button>
        </form>
      ) : (
        // No <form>, no editable <input>, no Save button — absent, not
        // disabled. A single-input <form onSubmit> would still submit on
        // Enter even with the button removed, so the read-only path renders
        // plain text instead of a disabled input.
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            Price adjustment
          </p>
          <p className="mt-1 text-sm">{percent}%</p>
        </div>
      )}

      {provider === 'airbnb' && mapping?.connection_id && (
        <AirbnbSettingsEditor connectionId={mapping.connection_id} canWrite={canWrite} />
      )}

      {/* Placeholder for taxes/fees — not yet wired in the API */}
      <fieldset disabled className="space-y-3 opacity-50">
        <legend className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Taxes &amp; fees <span className="ml-1 text-[0.6rem] normal-case tracking-normal">(coming soon)</span>
        </legend>
        <Input label="City tax (per guest/night)" type="number" step="0.01" disabled />
        <Input label="Cleaning fee" type="number" step="0.01" disabled />
      </fieldset>
    </div>
  );
}
